import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthenticationGuard } from './authentication.guard';

import { DashboardComponent } from './dashboard/dashboard.component';

import { FileviewerComponent } from './fileviewer/fileviewer.component';

import { TutorialsComponent } from './tutorials/tutorials.component';

import { ReleasenoteComponent } from './releasenote/releasenote.component';
import { ReleasehistoryComponent } from './releasehistory/releasehistory.component';
import { SettingsComponent } from './settings/settings.component';
import { NotificationsettingsComponent } from './settings/notificationsettings/notificationsettings.component';
import { CompliancePagesComponent } from './settings/compliance-pages/compliance-pages.component';
import { FaqSettingsComponent } from './settings/faq-settings/faq-settings.component';
import { NotificationsComponent } from './all-notifications/all-notifications.component';
import { SearchComponent } from './search/search.component';
import { ChangePasswordComponent } from './account/change-password/change-password.component';
import { TicketComponent } from './ticket/ticket.component';
import { ChatWithUsersComponent } from './chat-with-users/chat-with-users.component';
import { ViewTicketComponent } from './view-ticket/view-ticket.component';
import { VerifyEmailComponentComponent } from './verify-email-component/verify-email-component.component';
import { DevConsoleComponent } from './dev-console/dev-console.component';
import { GenerateKeyComponent } from './generate-key/generate-key.component';
import { ApikeyLogsComponent } from './apikey-logs/apikey-logs.component';
import { AccessDocsComponent } from './access-docs/access-docs.component';
import { ApiClientComponent } from './api-client/api-client.component';
import { DataMappingComponent } from './data-mapping/data-mapping.component';
import { ApiIntegrationListComponent } from './api-integration-list/api-integration-list.component';
import { NgVerticalTimelineComponent } from './shared/components/ng-vertical-timeline/ng-vetical-timeline.component';
import { RevisionHistoryResolver } from './shared/resolver/revisionHistory-resolver.resolver';
import { LandingPageComponent } from './landing-page/landing-page.component';
import { BookDemoComponent } from './book-demo/book-demo.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from './terms-and-conditions/terms-and-conditions.component';
import { CookiePolicyComponent } from './cookie-policy/cookie-policy.component';

const routes: Routes = [
  {
    path: '',
    component: LandingPageComponent,
    data: { showHeader: false, showSidebar: false, showFooter: false, hideFreshchat: true }
  },
  {
    path: 'book-demo',
    component: BookDemoComponent,
    data: { showHeader: false, showSidebar: false, showFooter: false, hideFreshchat: true }
  },
  {
    path: 'contact-us',
    component: ContactUsComponent,
    data: { showHeader: false, showSidebar: false, showFooter: false, hideFreshchat: true }
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
    data: { showHeader: false, showSidebar: false, showFooter: false, hideFreshchat: true }
  },
  {
    path: 'cookie-policy',
    component: CookiePolicyComponent,
    data: { showHeader: false, showSidebar: false, showFooter: false, hideFreshchat: true }
  },
  {
    path: 'terms-and-conditions',
    component: TermsAndConditionsComponent,
    data: { showHeader: false, showSidebar: false, showFooter: false, hideFreshchat: true }
  },
  {
    path: 'notifications',
    component: NotificationsComponent,
    data: {
      title: 'Notifications',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard',
        },
        {
          label: 'Notifications',
          url: ''
        }
      ],
    },
  },
  {
    path: 'search',
    children: [
      {
        path: '',
        component: SearchComponent,
        data: {
          title: 'Search Results',
          breadcrumb: [
            {
              label: 'Home',
              url: '/Dashboard',
            },
            {
              label: 'Search Results',
              url: ''
            }
          ],
        },
      },
    ],
    title: 'Certiplate - Search',
  },
  {
    path: 'tickets',
    component: TicketComponent,
    data: {
      title: 'Tickets',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard',
        },
        {
          label: 'Tickets',
          url: '/tickets'
        }
      ],
    },
  },
  {
    path: 'tickets/:id',
    component: TicketComponent,
    data: {
      title: 'Ticket Details',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard',
        },
        {
          label: 'Tickets',
          url: '/tickets'
        }
      ],
    },
  },
  {
    path: 'chat-with-users',
    component: ChatWithUsersComponent,
    data: {
      title: 'Chat with Users',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard',
        },
        {
          label: 'Chat with Users',
          url: '/chat-with-users'
        }
      ],
    },
  },
  {
    path: 'account',
    children: [
      {
        path: 'change-password',
        component: ChangePasswordComponent,
        data: {
          title: 'Change Password',
        },

      },

      {
        path: '',
        data: {
          showHeader: false, // Controlling Header for a Route
          showFooter: false,
        },

        loadChildren: () =>
          import('@app/account/account.module').then((m) => m.AccountModule),
      },
    ],
  },
  {
    path: 'verify-email/:token',
    component: VerifyEmailComponentComponent,
    data: {
      title: 'Verify Email',
      hideHeader: true,
      showHeader: false,
      showFooter: false,
      showSidebar: false
    }
  },
  {
    path: 'admin',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@app/admin/admin.module').then((m) => m.AdminModule),
      },
    ],
    title: 'Certiplate - Admin',
  },
  {
    path: 'Dashboard',
    component: DashboardComponent,
    data: {
      breadcrumb: 'Home',
    },
    title: 'Certiplate - Dashboard',
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'Setup',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@app/setup/setup.module').then((m) => m.SetupModule),
      },
    ],
    title: 'Certiplate - Setup',
  },
  {
    path: 'Finance',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@app/finance/finance.module').then((m) => m.FinanceModule),
      },
    ],
    title: 'Certiplate - Finance',
  },
  {
    path: 'Customer',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@app/sales/sales.module').then((m) => m.SalesModule),
      },
    ],
    title: 'Certiplate - Customer',
  },
  {
    path: 'Library',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@app/content/content.module').then((m) => m.ContentModule),
      },
    ],
    title: 'Certiplate - Library',
  },
  {
    path: 'AttachmentFile',
    component: FileviewerComponent,
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'AttachmentFile/:id',
    component: FileviewerComponent,
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'Assessments',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@app/operation/operation.module').then(
            (m) => m.OperationModule
          ),
      },
    ],
    title: 'Certiplate - Operations',
  },
  {
    path: 'UserSettings',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@app/resources/resources.module').then(
            (m) => m.ResourcesModule
          ),
      },
    ],
    title: 'Certiplate - UserSettings',
  },
  // {
  //   path: 'Logistics',
  //   children: [
  //     {
  //       path: '',
  //       loadChildren: () =>
  //         import('@app/logistics/logistics.module').then(
  //           (m) => m.LogisticsModule
  //         ),
  //     },
  //   ],
  //   title: 'Certiplate - Logistics',
  // },
  {
    path: 'Tutorials',
    component: TutorialsComponent,
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [AuthenticationGuard],
    children: [
      {
        path: '',
        redirectTo: 'faq-settings',
        pathMatch: 'full'
      },
      {
        path: 'notification-settings',
        component: NotificationsettingsComponent,
        data: {
          title: 'Notification Settings',
        },
      },
      {
        path: 'compliance-pages',
        component: CompliancePagesComponent,
        data: {
          title: 'Compliance Pages',
        },
      },
      {
        path: 'faq-settings',
        component: FaqSettingsComponent,
        data: {
          title: 'Help FAQ Management',
          breadcrumb: [
            {
              label: 'Home',
              url: '/Dashboard',
            },
            {
              label: 'Help FAQ Management',
              url: ''
            }
          ],
        },
      },
      {
        path: 'faq',
        component: FaqSettingsComponent,
        data: {
          title: 'Help FAQ Management',
          breadcrumb: [
            {
              label: 'Home',
              url: '/Dashboard',
            },
            {
              label: 'Help FAQ Management',
              url: ''
            }
          ],
        },
      },
    ],
    title: 'Certiplate - Settings',
  },


 {
  path: 'api-integration',
  component: ApiIntegrationListComponent,
  canActivate: [AuthenticationGuard],
  data: {
    title: 'API Integration',
    breadcrumb: [
      {
        label: 'Home',
        url: '/Dashboard'
      },
      {
        label: 'API Integration',
        url: '/api-integration'
      }
    ],
  },
},
{
  path: 'api-integration/add',
  component: ApiClientComponent,
  canActivate: [AuthenticationGuard],
  data: {
    title: 'Add API Integration',
    breadcrumb: [
      {
        label: 'Home',
        url: '/Dashboard'
      },
      {
        label: 'API Integration',
        url: '/api-integration'
      },
      {
        label: 'Add',
        url: ''
      }
    ],
  },
},
{
  path: 'api-integration/revision-history/:id/:name',
  component: NgVerticalTimelineComponent,
  canActivate: [AuthenticationGuard],
  resolve: { events: RevisionHistoryResolver },
  data: {
    title: 'API Integration Revision History',
    nameTitle: 'API Integration Revision History',
    breadcrumb: [
            { label: 'Home', url: '/Dashboard' },
            { label: 'API Integration', url: '/api-integration' },
            { label: 'API Integration Revsion History', disabled: true }
          ],
  }
},
{
  path: 'api-integration/edit/:id',
  component: ApiClientComponent,
  canActivate: [AuthenticationGuard],
  data: {
    title: 'Modify API Integration',
    breadcrumb: [
      {
        label: 'Home',
        url: '/Dashboard'
      },
      {
        label: 'API Integration',
        url: '/api-integration'
      },
      {
        label: 'Modify',
        url: ''
      }
    ],
  },
},

  {
    path: 'data-mapping',
    component: DataMappingComponent,
    canActivate: [AuthenticationGuard],
    data: {
      title: 'Data Mapping',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard'
        },
        {
          label: 'Data Mapping',
          url: '/data-mapping'
        }
      ],
    },
  },
  {
    path: 'dev-console',
    component: DevConsoleComponent,
    canActivate: [AuthenticationGuard],
    data: {
      title: 'Developer Console',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard'
        },
        {
          label: 'Developer Console',
          url: '/dev-console'
        }
      ],
    },
  },
  {
    path: 'generate-key',
    component: GenerateKeyComponent,
    canActivate: [AuthenticationGuard],
    data: {
      title: 'Generate Key',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard'
        },
        {
          label: 'Developer Console',
          url: '/dev-console'
        },
        {
          label: 'Generate Key',
          url: '/generate-key'
        }
      ],
    },
  },
  {
    path: 'view-logs/:keyId',
    component: ApikeyLogsComponent,
    canActivate: [AuthenticationGuard],
    data: {
      title: 'View Logs',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard'
        },
        {
          label: 'Developer Console',
          url: '/dev-console'
        },
        {
          label: 'View logs',
          url: '/view-logs'
        }
      ],
    },
  },
  {
    path: 'access-docs',
    component: AccessDocsComponent,
    canActivate: [AuthenticationGuard],
    data: {
      title: 'Access Documentation',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard'
        },
        {
          label: 'Developer Console',
          url: '/dev-console'
        },
        {
          label: 'Access Docs',
          url: '/access-docs'
        }
      ],
    },
  },
  {
    path: 'releasenotes',
    component: ReleasenoteComponent,
    data: {
      title: 'Release Notes',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard'
        },
        {
          label: 'Current Release',
          url: '/releasenotes'
        }
      ],
    }
  },
  {
    path: 'releasenotes/:key',
    component: ReleasenoteComponent,
    data: {
      title: 'Release Notes',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard'
        },
        {
          label: 'Current Release',
          url: '/releasenotes'
        }
      ]
    }
  },
  {
    path: 'releasenotes/:key',
    component: ReleasenoteComponent,
    data: { title: 'Release Notes' }
  },
  {
    path: 'releasehistory',
    component: ReleasehistoryComponent,
    data: {
      title: 'Release Notes',
      breadcrumb: [
        {
          label: 'Home',
          url: '/Dashboard'
        },
        {
          label: 'Release History',
          url: '/releasehistory'
        }
      ],
    },
  },

  {
    path: 'view-ticket',
    component: ViewTicketComponent,
    data: {
      title: 'Ticket-detail',
      breadcrumb: [
        {
          label: 'Home', // Label for the breadcrumb
          url: '/Dashboard'   // URL to navigate to when clicked
        },
         {
          label: 'Ticket',
          url: '/tickets'
        },
         {
          label: 'Ticket-detail',
          url: '/view-ticket'
        }
      ],
    },
  },
  {
    path: 'tutorials',
    loadChildren: () => import('./tutorialsection/tutorialsection.module').then(m => m.TutorialsectionModule),
    data: { hideLayout: true }
  },
  { path: '**', redirectTo: 'account', pathMatch: 'full' }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Restore the last scroll position
      scrollPositionRestoration: 'enabled',
      scrollOffset: [0, 0],
      // Enable scrolling to anchors
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
